import { Link } from "react-router-dom";
import { Banknote, ArrowLeft } from "lucide-react";
import Seo from "../components/Seo";

const Section = ({ title, children }) => (
  <section className="mt-7">
    <h2 className="font-sora text-lg font-semibold text-[#0B1220]">{title}</h2>
    <div className="mt-2 text-sm text-[#475569] leading-relaxed space-y-2">{children}</div>
  </section>
);

/* Combined £50 cashback terms covering BOTH approved MortgagePath cashback
   rewards (survey + conveyancing). Served at /reward-terms and /survey-reward-terms. */
const RewardTerms = () => (
  <div className="mp-container py-14 max-w-3xl" data-testid="reward-terms">
    <Seo
      title="£50 Cashback — Terms & Conditions | MortgagePath"
      description="Terms and conditions for the MortgagePath £50 survey and £50 conveyancing cashback rewards — up to £100 in total."
      path="/reward-terms"
      noindex
    />
    <Link to="/rewards" className="inline-flex items-center gap-1.5 text-sm text-[#0F766E] hover:underline" data-testid="rt-back">
      <ArrowLeft className="w-4 h-4" /> Back to cashback
    </Link>

    <div className="mt-6 flex items-center gap-3">
      <span className="w-11 h-11 rounded-xl bg-[#0F766E]/10 border border-[#0F766E]/20 grid place-items-center shrink-0" aria-hidden="true">
        <Banknote className="w-5 h-5 text-[#0F766E]" />
      </span>
      <div>
        <div className="text-[11px] uppercase tracking-widest text-[#0F766E]">MortgagePath cashback</div>
        <h1 className="font-sora text-3xl sm:text-4xl font-bold tracking-tight text-[#0B1220]">£50 Cashback — Terms &amp; Conditions</h1>
      </div>
    </div>

    <p className="mt-5 text-sm text-[#475569] leading-relaxed">
      These terms govern the MortgagePath cashback rewards ("the cashback"). There are currently two separate cashback
      rewards: £50 cashback for completing a qualifying property survey through MortgagePath, and £50 cashback for
      completing qualifying conveyancing through MortgagePath — up to £100 in total. The cashback is funded and
      administered by MortgagePath. It is not a discount, and is not provided, funded or endorsed by our survey or
      conveyancing providers, and it does not affect the service you receive, the price you are quoted, or any
      professional advice or recommendations made by the provider.
    </p>

    <Section title="1. Promoter">
      <p>The promoter and organiser is Home Finance Group Ltd, trading as MortgagePath ("MortgagePath", "we", "us").</p>
    </Section>

    <Section title="2. Eligibility (both cashback rewards)">
      <ul className="list-disc pl-5 space-y-1.5">
        <li>You must be aged 18 or over.</li>
        <li>You must be resident in the United Kingdom, and the property concerned must be located in England or Wales.</li>
        <li>The cashback is available to the person who submits the relevant quote request through MortgagePath.</li>
        <li>MortgagePath employees, contractors and their immediate families are not eligible.</li>
      </ul>
    </Section>

    <Section title="3. £50 Property Survey Cashback">
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Request a property survey quote through MortgagePath and consent to your details being shared with our selected survey provider.</li>
        <li>Go ahead and complete your qualifying survey with that provider using the MortgagePath referral — meaning the survey is booked, carried out and paid for in full.</li>
        <li>On completion, and once we can verify it with our provider, £50 cashback becomes payable.</li>
      </ul>
    </Section>

    <Section title="4. £50 Conveyancing Cashback">
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Request a conveyancing quote through MortgagePath and consent to your details being shared with our selected conveyancing provider.</li>
        <li>Go ahead and complete your qualifying conveyancing with that provider using the MortgagePath referral — meaning your conveyancing matter completes and the provider's fees are paid in full.</li>
        <li>On completion, and once we can verify it with our provider, £50 cashback becomes payable.</li>
      </ul>
    </Section>

    <Section title="5. Using both services — up to £100">
      <p>
        Each cashback reward is available independently; you do not need to use both services to qualify for an
        individual £50 payment. The two rewards are entirely separate and are assessed independently. If you complete
        both a qualifying survey and qualifying conveyancing through MortgagePath, you can qualify for both — up to
        £100 cashback in total — provided the requirements for each are met in full. £100 cashback is not available for
        completing only one service.
      </p>
    </Section>

    <Section title="6. The cashback">
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Each reward is a £50 cash payment (up to £100 in total where both services qualify).</li>
        <li>One payment is available per qualifying service, per property, per household.</li>
        <li>The cashback is non-transferable.</li>
      </ul>
    </Section>

    <Section title="7. A quote or instruction alone does not qualify">
      <p>
        Requesting a quote, or instructing a provider, does not by itself establish any entitlement to cashback.
        Cashback depends on the relevant qualifying service actually completing (and being paid for in full) and on
        our successful verification of that completion with the provider.
      </p>
    </Section>

    <Section title="8. How the cashback is paid">
      <p>
        Cashback is paid by secure UK bank transfer after we verify your completed service. We will request your bank
        details through a secure channel at that stage — never via a website form or ordinary email. Please allow up to
        45 days from completion and successful verification for the cashback to be paid.
      </p>
      <p className="text-xs text-[#94A3B8]">Payment method shown is subject to final confirmation.</p>
    </Section>

    <Section title="9. How to claim">
      <p>
        Once your qualifying service has completed, email{" "}
        <a href="mailto:info@mortgagepath.uk" className="text-[#0F766E] underline">info@mortgagepath.uk</a> with your
        MortgagePath reference (shown when you requested your quote) so we can verify your claim and arrange payment. We
        use the minimum personal information necessary to identify and verify your claim.
      </p>
    </Section>

    <Section title="10. Exclusions">
      <ul className="list-disc pl-5 space-y-1.5">
        <li>Cashback is not available if you do not proceed, cancel the service, or arrange the service outside the MortgagePath referral.</li>
        <li>Requesting a quote alone does not qualify — the relevant service must be completed and paid for.</li>
        <li>MortgagePath may vary, suspend or withdraw either promotion, or amend these terms, at any time. Changes do not affect cashback already validly earned.</li>
      </ul>
    </Section>

    <Section title="11. Your data">
      <p>
        We handle your personal data in line with our{" "}
        <Link to="/privacy" className="text-[#0F766E] underline">Privacy Policy</Link>. Requesting a quote is
        no-obligation; your details are only shared with a provider after you give consent, and each service requires
        its own separate consent.
      </p>
    </Section>

    <Section title="12. Contacting us about a claim">
      <p>
        For any question about a cashback claim, email{" "}
        <a href="mailto:info@mortgagepath.uk" className="text-[#0F766E] underline">info@mortgagepath.uk</a> with your
        MortgagePath reference.
      </p>
    </Section>

    <p className="mt-8 text-xs text-[#94A3B8] leading-relaxed">
      MortgagePath provides educational information only and is not a surveyor or a conveyancer. Where you request a
      survey or conveyancing quote through MortgagePath, we may receive a referral fee. This does not increase what you
      pay and never affects the educational information we provide. The £50 cashback rewards are separate MortgagePath
      promotions, funded and administered by MortgagePath.
    </p>
  </div>
);

export default RewardTerms;
